import type { RawTrawledPaper } from "@/services/dedup";

/**
 * Stratify papers into a blended set of seminal, recent, and methodological papers.
 * Takes top N/3 from each bucket, deduplicates overlaps, fills gaps from remainder.
 * Falls back to input order when citation metadata is missing.
 */
export function stratifyPapers(papers: RawTrawledPaper[], limit: number): RawTrawledPaper[] {
  if (papers.length <= limit) return papers;

  const hasMetadata = papers.some(
    (p) => (p.citations ?? 0) > 0 || (p.year ?? 0) > 0 || (p.influentialCitationCount ?? 0) > 0,
  );
  if (!hasMetadata) return papers.slice(0, limit);

  const bucketSize = Math.max(1, Math.ceil(limit / 3));
  const selected = new Set<string>();
  const result: RawTrawledPaper[] = [];

  const addUnique = (paper: RawTrawledPaper) => {
    const key = paper.paper_id ?? paper.doi ?? paper.title;
    if (!key || selected.has(key)) return false;
    selected.add(key);
    result.push(paper);
    return true;
  };

  // Bucket 1: Seminal — top by citationCount
  const byCitations = [...papers].sort((a, b) => (b.citations ?? 0) - (a.citations ?? 0));
  for (const p of byCitations) {
    if (result.length >= bucketSize) break;
    addUnique(p);
  }

  // Bucket 2: Recent — top by year desc, tiebreak by citationCount
  const byRecency = [...papers].sort((a, b) => {
    const yearDiff = (b.year ?? 0) - (a.year ?? 0);
    return yearDiff !== 0 ? yearDiff : (b.citations ?? 0) - (a.citations ?? 0);
  });
  const recentTarget = bucketSize * 2;
  for (const p of byRecency) {
    if (result.length >= recentTarget) break;
    addUnique(p);
  }

  // Bucket 3: Methodological — top by influentialCitationCount
  const byInfluential = [...papers].sort(
    (a, b) => (b.influentialCitationCount ?? 0) - (a.influentialCitationCount ?? 0),
  );
  const methodTarget = bucketSize * 3;
  for (const p of byInfluential) {
    if (result.length >= methodTarget) break;
    addUnique(p);
  }

  // Fill remaining slots from unselected papers (original order)
  for (const p of papers) {
    if (result.length >= limit) break;
    addUnique(p);
  }

  return result.slice(0, limit);
}
